import { useNavigate } from "react-router-dom";
import { AdminModal } from "../../admin-panel/components/AdminModal";
import { ClockIcon,WarningIcon } from "../../icons";

interface TrialExpiryModalProps {
  open: boolean;
  onClose: () => void;
  daysRemaining: number;
  isExpired: boolean;
}

const FEATURES_LOSING = [
  "AI-powered insights (iSight)",
  "Executive Scaleboard",
  "Daily WhatsApp & Email reports",
  "Automated business alerts",
  "Marketing, inventory, and performance intelligence",
  "Data synchronization with your connected store",
];

export function TrialExpiryModal({
  open,
  onClose,
  daysRemaining,
  isExpired,
}: TrialExpiryModalProps) {
  const navigate = useNavigate();

  const handleUpgrade = () => {
    onClose();
    navigate("/billing");
  };

  return (
    <AdminModal open={open} onClose={onClose} closeOnOverlayClick closeOnEscape zIndexBase={1100}>
      {/* <style>
        {`
          @keyframes clock-hour-spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          @keyframes clock-minute-spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          // @keyframes clock-second-spin {
          //   from { transform: rotate(0deg); }
          //   to { transform: rotate(360deg); }
          // }
        `}
      </style> */}
      <div className="relative w-full max-w-lg rounded-2xl border border-slate-200 bg-white p-6 shadow-xl dark:border-[#FFFFFF0F] dark:bg-[#111f33]">
        {/* Close button */}
        {/* <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 dark:hover:bg-white/10 dark:hover:text-slate-200"
        >
          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button> */}

        <button
          type="button"
          onClick={onClose}
          className="absolute right-5 top-5 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 transition-colors"
          aria-label="Close"
        >
          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Icon */}
        <div className="mb-4 flex justify-center">
          <div
            className={`flex h-12 w-12 items-center justify-center rounded-full ${isExpired
                ? "bg-red-100 dark:bg-red-900/40"
                : "bg-amber-100 dark:bg-amber-900/40"
              }`}
          >
            {isExpired ? (
             <WarningIcon className="h-5 w-5 text-red-600 dark:text-red-400" />
            ) : (
              <ClockIcon className="h-5 w-5 text-amber-600 dark:text-amber-400" />
              // <svg
              //   className="h-7 w-7 text-amber-600 dark:text-amber-400"
              //   fill="none"
              //   stroke="currentColor"
              //   viewBox="0 0 24 24"
              //   aria-hidden
              // >
              //   {/* Clock face */}
              //   <circle cx="12" cy="12" r="9" strokeWidth="2" />

              //   {/* Hour hand - slow rotation */}
              //   <line
              //     x1="12"
              //     y1="12"
              //     x2="12"
              //     y2="8"
              //     strokeWidth="2"
              //     strokeLinecap="round"
              //     style={{
              //       transformOrigin: "12px 12px",
              //       animation: "clock-hour-spin 8s linear infinite",
              //     }}
              //   />

              //   {/* Minute hand - medium rotation */}
              //   <line
              //     x1="12"
              //     y1="12"
              //     x2="12"
              //     y2="6.5"
              //     strokeWidth="1.5"
              //     strokeLinecap="round"
              //     style={{
              //       transformOrigin: "12px 12px",
              //       animation: "clock-minute-spin 4s linear infinite",
              //     }}
              //   />

              //   {/* Second hand - fast rotation, thin */}
              //   {/* <line
              //     x1="12"
              //     y1="12"
              //     x2="12"
              //     y2="5.5"
              //     strokeWidth="1"
              //     strokeLinecap="round"
              //     style={{
              //       transformOrigin: "12px 12px",
              //       animation: "clock-second-spin 2s linear infinite",
              //     }}
              //   /> */}

              //   {/* Center dot */}
              //   <circle cx="12" cy="12" r="0.7" fill="currentColor" stroke="none" />
              // </svg> 
            )}
          </div>
        </div>

        {/* Title */}
        <h3 className="mb-2 text-center text-xl font-semibold text-slate-900 dark:text-white">
          {isExpired ? "Your Trial Has Expired" : "Your Trial is Ending Soon"}
        </h3>

        {/* Subtitle */}
        <p className="mb-4 text-center text-sm text-slate-600 dark:text-slate-300">
          {isExpired
            ? "Your free trial has ended. Upgrade now to continue using all features."
            : (
              <>
                You have{" "}
                <span className="text-base font-bold text-slate-900 dark:text-white">
                  {daysRemaining} day{daysRemaining !== 1 ? "s" : ""}
                </span>{" "}
                remaining in your free trial.
              </>
            )}
        </p>

        {/* Features list */}
        <div className="mb-5 rounded-lg border border-slate-100 bg-slate-50 p-4 dark:border-[#FFFFFF0F] dark:bg-[#0d1828]">
          {!isExpired && (
            <p className="mb-3 text-left text-sm font-semibold text-slate-800 dark:text-slate-200">
              Upgrade to keep access to:
            </p>
          )}
          <ul className="space-y-2">
            {FEATURES_LOSING.map((feature, idx) => (
              <li
                key={idx}
                className="flex items-start gap-2 text-[13px] text-slate-700 dark:text-slate-300"
              >
                <span
                  className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-full ${isExpired
                      ? "bg-red-100 dark:bg-red-900/30"
                      : "bg-brand-100 dark:bg-brand-900/30"
                    }`}
                >
                  {isExpired ? (
                    <svg
                      className="h-2.5 w-2.5 text-red-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="3"
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  ) : (
                    <svg
                      className="h-2.5 w-2.5 text-brand-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="3"
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  )}
                </span>
                {feature}
              </li>
            ))}
          </ul>
        </div>

        {/* Buttons */}
        <div className="flex justify-end gap-3">
          <button
            type="button"
            onClick={handleUpgrade}
            className="rounded-[6px] bg-teal-600 px-4 py-2 text-sm font-semibold text-white hover:bg-teal-700"
          >
            Upgrade Plan
          </button>
          {/* <button
            type="button"
            onClick={onClose}
            className="w-full rounded-lg border border-slate-200 px-4 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 dark:border-[#FFFFFF0F] dark:text-slate-300 dark:hover:bg-white/5"
          >
            {isExpired ? "Remind Me Later" : "Continue with Trial"}
          </button> */}
        </div>
      </div>
    </AdminModal>
  );
}
